// @workflow assistant.clarification
/**
 * Clarification requests (contract "Clarifications: ask, don't guess"): when AVA can't tell what
 * the user means it asks. One request carries 1–3 questions (Khalid, 2026-10-14: like Claude's
 * AskUserQuestion), each with 2–4 options, and the composer is the free-text answer.
 *
 * This leaf module owns the rules and words: it checks and cleans an incoming request (the new
 * `questions` shape, and the old one-question shape stored history still holds), cleans one
 * question's picks, and phrases the compact line the thread keeps. Whole answers are in
 * clarificationAnswers.js, stepping through the questions in clarificationSteps.js, and state
 * changes on the conversation in clarificationState.js.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { REQUEST_STATUS } from './clientRequests.js';

/** Contract limits on a clarification. */
export const CLARIFICATION_LIMITS = Object.freeze({
  MIN_QUESTIONS: 1,
  MAX_QUESTIONS: 3,
  MIN_OPTIONS: 2,
  MAX_OPTIONS: 4,
  ID_LENGTH: 32,
  LABEL_LENGTH: 40,
  DESCRIPTION_LENGTH: 90,
});

/** Question and option ids are short and stable: letters, digits, `-` and `_` (`findings`, `p101-u2`). */
const SHORT_ID = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;

/** The id of the one question a request in the old shape (top-level `question`, no `questions`) asked. */
export const LEGACY_QUESTION_ID = 'q1';

/** Why a request was refused; the UI logs it and answers `{ answer: { dismissed: true } }`. */
export const INVALID_REASON = Object.freeze({
  QUESTION_COUNT: `it needs ${CLARIFICATION_LIMITS.MIN_QUESTIONS} to ${CLARIFICATION_LIMITS.MAX_QUESTIONS} questions`,
  QUESTION_ID: `a question id is missing, longer than ${CLARIFICATION_LIMITS.ID_LENGTH} or not a short id`,
  DUPLICATE_QUESTION_ID: 'two questions share an id',
  QUESTION: 'the question is missing',
  OPTION_COUNT: `it needs ${CLARIFICATION_LIMITS.MIN_OPTIONS} to ${CLARIFICATION_LIMITS.MAX_OPTIONS} options`,
  OPTION_ID: `an option id is missing, longer than ${CLARIFICATION_LIMITS.ID_LENGTH} or not a short id`,
  DUPLICATE_ID: 'two options share an id',
  LABEL: `an option label is missing or longer than ${CLARIFICATION_LIMITS.LABEL_LENGTH}`,
  DESCRIPTION: `an option description is not text or is longer than ${CLARIFICATION_LIMITS.DESCRIPTION_LENGTH}`,
});

/** The answer for a closed panel (× or Esc, on any step): the model doesn't guess, it waits for the detail. */
export const DISMISSED_ANSWER = Object.freeze({ dismissed: true });

/** Every word the clarification shows, in one place. */
export const CLARIFICATION_TEXT = Object.freeze({
  WAITING: 'AVA asked you a question · answer below',
  CHOSE: 'You chose: ',
  ANSWERED: 'You answered: ',
  AND_ANSWERED: ', and answered: ',
  AND_WROTE: ', and wrote ',
  ANSWERED_UNKNOWN: 'You answered the question',
  DISMISSED: 'You dismissed the question',
  DISMISSED_MANY: 'You dismissed the questions',
  EXPIRED: 'Expired before you answered',
  UNANSWERED: 'Not answered',
  WILL_QUOTE: 'Your next message quotes this question, so AVA can pick it up.',
  WILL_QUOTE_MANY: 'Your next message quotes these questions, so AVA can pick them up.',
  PICK_ONE: 'Pick one',
  PICK_MANY: 'Pick one or more',
  OR_TYPE: ', or type your own answer below.',
  CHOOSE: 'choose',
  TOGGLE: 'toggle',
  DISMISS: 'Dismiss the question',
  DISMISS_MANY: 'Dismiss the questions',
  KEY_DISMISS: 'dismiss',
  KEY_QUESTION: 'question',
  SELECTED: 'selected',
  CONTINUE: 'Continue',
  NEXT: 'Next',
  SUBMIT: 'Submit',
  PREVIOUS_QUESTION: 'Previous question',
  NEXT_QUESTION: 'Next question',
  SENT: 'Answer sent.',
  CLOSED: 'Question dismissed.',
  CLOSED_MANY: 'Questions dismissed.',
  PLACEHOLDER: 'Or type your own answer',
  LOCKED_PLACEHOLDER: 'Pick an option above',
  SEND: 'Send answer',
  COMMANDS_OFF: 'Commands are off while you answer',
  CHIP_TITLE: "What you type answers AVA's question",
});

/** The words that count: "1 of 3", "AVA asked you 2 questions", … */
export const CLARIFICATION_COUNTED = Object.freeze({
  step: (index, count) => `${index + 1} of ${count}`,
  answered: (done, count) => `${done} of ${count} answered`,
  waiting: (count) => `AVA asked you ${count} questions · answer below`,
  answeredAll: (count) => `You answered AVA's ${count} questions`,
});

const LINE_ICON = Object.freeze({ WAITING: 'question-mark-circle', DONE: 'check-circle', CLOSED: 'x-mark', EXPIRED: 'clock' });
export const LINE_TONE = Object.freeze({ WAITING: 'waiting', DONE: 'done', CLOSED: 'closed' });

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');
const quoted = (text) => `“${text}”`;
const refuse = (reason) => ({ ok: false, reason });
const isShortId = (id) => !!id && id.length <= CLARIFICATION_LIMITS.ID_LENGTH && SHORT_ID.test(id);
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

/** The questions of a clarification part (or request), [] when there are none. */
export const questionsOf = (part) => (part && Array.isArray(part.questions) ? part.questions : []);

function cleanOption(raw) {
  if (!raw || typeof raw !== 'object') return { reason: INVALID_REASON.OPTION_ID };
  const id = trimmed(raw.id);
  if (!isShortId(id)) return { reason: INVALID_REASON.OPTION_ID };
  const label = trimmed(raw.label);
  if (!label || label.length > CLARIFICATION_LIMITS.LABEL_LENGTH) return { reason: INVALID_REASON.LABEL };
  if (raw.description === undefined || raw.description === null) return { option: { id, label } };
  if (typeof raw.description !== 'string') return { reason: INVALID_REASON.DESCRIPTION };
  const description = raw.description.trim();
  if (description.length > CLARIFICATION_LIMITS.DESCRIPTION_LENGTH) return { reason: INVALID_REASON.DESCRIPTION };
  return { option: description ? { id, label, description } : { id, label } };
}

function cleanQuestion(raw) {
  if (!raw || typeof raw !== 'object') return { reason: INVALID_REASON.QUESTION_ID };
  const id = trimmed(raw.id);
  if (!isShortId(id)) return { reason: INVALID_REASON.QUESTION_ID };
  const question = trimmed(raw.question);
  if (!question) return { reason: INVALID_REASON.QUESTION };
  const list = raw.options;
  if (!Array.isArray(list) || list.length < CLARIFICATION_LIMITS.MIN_OPTIONS || list.length > CLARIFICATION_LIMITS.MAX_OPTIONS) {
    return { reason: INVALID_REASON.OPTION_COUNT };
  }
  const cleaned = list.map(cleanOption);
  const bad = cleaned.find((r) => r.reason);
  if (bad) return { reason: bad.reason };
  const options = cleaned.map((r) => r.option);
  if (new Set(options.map((o) => o.id)).size !== options.length) return { reason: INVALID_REASON.DUPLICATE_ID };
  return { question: { id, question, options, allowFreeText: raw.allowFreeText !== false, multiSelect: raw.multiSelect === true } };
}

/** The old one-question shape, read as a list of one (stored history replays it). */
const legacyQuestions = (raw) => [{ id: LEGACY_QUESTION_ID, question: raw.question, options: raw.options, allowFreeText: raw.allowFreeText, multiSelect: raw.multiSelect }];

/**
 * Checks a `client.request { kind: 'clarification' }` against the contract and keeps only what the
 * panel shows. A request that breaks a rule for any one question is refused whole: the UI never
 * shows a half question. A `questions` key that is there but malformed is refused, never read as
 * the old shape.
 * @param {object} raw the event
 * @returns {{ ok: true, value: { questions: Array<{ id: string, question: string, options: Array<{ id: string, label: string, description?: string }>, allowFreeText: boolean, multiSelect: boolean }> } } | { ok: false, reason: string }}
 */
export function cleanClarification(raw) {
  if (!raw || typeof raw !== 'object') return refuse(INVALID_REASON.QUESTION);
  const list = hasOwn(raw, 'questions') ? raw.questions : legacyQuestions(raw);
  if (!Array.isArray(list) || list.length < CLARIFICATION_LIMITS.MIN_QUESTIONS || list.length > CLARIFICATION_LIMITS.MAX_QUESTIONS) {
    return refuse(INVALID_REASON.QUESTION_COUNT);
  }
  const cleaned = list.map(cleanQuestion);
  const bad = cleaned.find((r) => r.reason);
  if (bad) return refuse(bad.reason);
  const questions = cleaned.map((r) => r.question);
  if (new Set(questions.map((q) => q.id)).size !== questions.length) return refuse(INVALID_REASON.DUPLICATE_QUESTION_ID);
  return { ok: true, value: { questions } };
}

function answerOf(ids, text) {
  if (!ids.length && !text) return null;
  return text ? { optionIds: ids, text } : { optionIds: ids };
}

/**
 * One question's answer: known option ids in the panel's order (one for single-select), plus the
 * typed text when free text is allowed. Null when there is nothing to send.
 * @param {{ options: Array<{ id: string }>, multiSelect: boolean, allowFreeText: boolean }} question
 * @param {{ optionIds?: string[], text?: string }} picked
 * @returns {{ optionIds: string[], text?: string } | null}
 */
export function buildAnswer(question, { optionIds = [], text = '' } = {}) {
  const wanted = new Set(Array.isArray(optionIds) ? optionIds : []);
  const known = question.options.map((o) => o.id).filter((id) => wanted.has(id));
  return answerOf(question.multiSelect ? known : known.slice(0, 1), question.allowFreeText ? trimmed(text) : '');
}

/** What the UI posts to `…/client-requests/{requestId}`. */
export const answerBody = (answer) => ({ answer });

/** The request's state once answered: dismissed reads as cancelled, anything else as done. */
export const statusForAnswer = (answer) => (answer && answer.dismissed ? REQUEST_STATUS.CANCELLED : REQUEST_STATUS.DONE);

const labelsOf = (answer, options) => answer.optionIds.map((id) => options.find((o) => o.id === id)).filter(Boolean).map((o) => o.label);

/** "You chose: Findings, and answered: “this year”" — the line of a one-question clarification. */
function answerSegments(answer, options) {
  const labels = labelsOf(answer, options);
  const chose = labels.length ? [{ text: CLARIFICATION_TEXT.CHOSE }, { text: labels.join(', '), strong: true }] : [];
  if (!answer.text) return chose.length ? chose : [{ text: CLARIFICATION_TEXT.ANSWERED_UNKNOWN }];
  const lead = labels.length ? CLARIFICATION_TEXT.AND_ANSWERED : CLARIFICATION_TEXT.ANSWERED;
  return [...chose, { text: lead }, { text: quoted(answer.text), strong: true }];
}

/** "Both, and wrote “this year”" — one question's answer beside the question itself. */
function itemSegments(answer, options) {
  const labels = labelsOf(answer, options);
  const chose = labels.length ? [{ text: labels.join(', '), strong: true }] : [];
  if (!answer.text) return chose;
  return [...chose, ...(labels.length ? [{ text: CLARIFICATION_TEXT.AND_WROTE }] : []), { text: quoted(answer.text), strong: true }];
}

const answerTo = (answer, question) => (answer.answers || []).find((a) => a.questionId === question.id) || { optionIds: [] };

function closedLead(status, answer, many) {
  const line = (tone, icon, text) => ({ tone, icon, segments: [{ text }] });
  if ((answer && answer.dismissed) || (!answer && status === REQUEST_STATUS.CANCELLED)) {
    return line(LINE_TONE.CLOSED, LINE_ICON.CLOSED, many ? CLARIFICATION_TEXT.DISMISSED_MANY : CLARIFICATION_TEXT.DISMISSED);
  }
  if (status === REQUEST_STATUS.DONE) return line(LINE_TONE.DONE, LINE_ICON.DONE, CLARIFICATION_TEXT.ANSWERED_UNKNOWN);
  if (status === REQUEST_STATUS.EXPIRED) return line(LINE_TONE.CLOSED, LINE_ICON.EXPIRED, CLARIFICATION_TEXT.EXPIRED);
  return line(LINE_TONE.CLOSED, LINE_ICON.CLOSED, CLARIFICATION_TEXT.UNANSWERED);
}

function leadFor({ status, answer }, questions) {
  const many = questions.length > 1;
  if (status === REQUEST_STATUS.PENDING) {
    return { tone: LINE_TONE.WAITING, icon: LINE_ICON.WAITING, segments: [{ text: many ? CLARIFICATION_COUNTED.waiting(questions.length) : CLARIFICATION_TEXT.WAITING }] };
  }
  if (!answer || answer.dismissed || !Array.isArray(answer.answers)) return closedLead(status, answer, many);
  if (!many) return { tone: LINE_TONE.DONE, icon: LINE_ICON.DONE, segments: answerSegments(answerTo(answer, questions[0]), questions[0].options) };
  return {
    tone: LINE_TONE.DONE,
    icon: LINE_ICON.DONE,
    segments: [{ text: CLARIFICATION_COUNTED.answeredAll(questions.length) }],
    items: questions.map((q) => ({ id: q.id, question: q.question, segments: itemSegments(answerTo(answer, q), q.options) })),
  };
}

function askedLine(questions) {
  if (questions.length <= 1) return `AVA asked ${quoted(questions.length ? questions[0].question : '')}`;
  return `AVA asked ${questions.length} questions: ${quoted(questions[0].question)} and ${questions.length - 1} more`;
}

/**
 * The line the thread keeps for a clarification ("You chose: Findings · AVA asked “Which do you
 * mean?”"); history replays it the same way. Several answered questions add `items`, one per
 * question: the question and its answer, readable on its own. `segments` render as text, `strong`
 * ones in bold.
 * @param {{ status: string, answer?: object|null, questions: Array<object> }} part
 * @returns {{ tone: string, icon: string, segments: Array, text: string, asked: string, items?: Array<{ id: string, question: string, segments: Array }> }}
 */
export function clarificationLine(part) {
  const questions = questionsOf(part);
  const { tone, icon, segments, items } = leadFor(part, questions);
  const line = { tone, icon, segments, text: segments.map((s) => s.text).join(''), asked: askedLine(questions) };
  return items ? { ...line, items } : line;
}

/** The panel's line under the question. */
export function pickHint({ multiSelect, allowFreeText }) {
  const pick = multiSelect ? CLARIFICATION_TEXT.PICK_MANY : CLARIFICATION_TEXT.PICK_ONE;
  return allowFreeText ? `${pick}${CLARIFICATION_TEXT.OR_TYPE}` : `${pick}.`;
}

/** What the live region reads when the clarification opens. */
export function askedAnnouncement(part) {
  const questions = questionsOf(part);
  const first = questions[0];
  if (!first) return '';
  if (questions.length === 1) return `AVA asks: ${first.question} ${first.options.length} options.`;
  return `AVA asks ${questions.length} questions. Question 1: ${first.question} ${first.options.length} options.`;
}

/** What the live region reads when the panel moves to another question. */
export function stepAnnouncement(part, index) {
  const questions = questionsOf(part);
  const q = questions[index];
  return q ? `Question ${CLARIFICATION_COUNTED.step(index, questions.length)}: ${q.question} ${q.options.length} options.` : '';
}

/**
 * How the composer reads while a question is open: it is the free-text answer to the question on
 * screen (`index`), or locked when that question allows no free text. Null when none is open.
 */
export function answeringMode(part, index = 0) {
  const questions = questionsOf(part);
  const q = questions[index];
  if (!q) return null;
  const locked = !q.allowFreeText;
  const lead = questions.length > 1 ? `Answering ${CLARIFICATION_COUNTED.step(index, questions.length)}` : 'Answering';
  return {
    locked,
    chip: `${lead}: ${q.question}`,
    inputLabel: `Your answer to: ${q.question}`,
    placeholder: locked ? CLARIFICATION_TEXT.LOCKED_PLACEHOLDER : CLARIFICATION_TEXT.PLACEHOLDER,
    sendLabel: CLARIFICATION_TEXT.SEND,
    commandsLabel: CLARIFICATION_TEXT.COMMANDS_OFF,
  };
}

/**
 * How a message typed after the clarification expired quotes it, so nothing is lost.
 * @param {string[]|string} questions the question texts
 */
export function quotePrefix(questions) {
  const list = (Array.isArray(questions) ? questions : [questions]).map(quoted);
  if (list.length <= 1) return `About your question ${list[0] || quoted('')}: `;
  return `About your questions ${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}: `;
}
